import React, { useMemo, useState } from 'react';
import styles from './ContactPage.module.css';
import { submitContact } from '../api/contact';
import { useTranslation } from 'react-i18next';

const initialForm = {
  name: '',
  email: '',
  inquiry_type: '一般のお問い合わせ',
  subject: '',
  message: '',
  event_url: '',
};

const ContactPage = () => {
  const [form, setForm] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [inquiryCode, setInquiryCode] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation('contact');

  const categories = useMemo(
    () => [
      { value: '一般のお問い合わせ', label: t('categories.general') },
      { value: 'チケット購入について', label: t('categories.ticket') },
      { value: '販売者登録・売上について', label: t('categories.seller') },
      { value: '不正・違反報告', label: t('categories.violation') },
      { value: 'その他', label: t('categories.other') },
    ],
    [t]
  );

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const next = {};

    if (!form.name.trim()) next.name = t('error.name');
    if (!form.email.trim()) {
      next.email = t('error.email');
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      next.email = t('error.emailFormat');
    }

    if (!form.inquiry_type.trim()) {
      next.inquiry_type = t('error.category');
    }

    if (form.subject && form.subject.length > 255) {
      next.subject = t('error.subjectLength');
    }

    if (!form.message.trim()) {
      next.message = t('error.message');
    } else if (form.message.trim().length < 10) {
      next.message = t('error.messageLength');
    }

    if (form.event_url && !/^https?:\/\//.test(form.event_url)) {
      next.event_url = t('error.eventUrlFormat');
    }

    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setInquiryCode('');

    const nextErrors = validate();
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitted(false);
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        inquiry_type: form.inquiry_type,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        event_url: form.event_url.trim() || null,
      });

      setSubmitted(true);
      setInquiryCode(result?.inquiry_code || '');
      setForm(initialForm);
      setFieldErrors({});
    } catch (err) {
      setSubmitted(false);
      const backendErrors = err?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        const normalized = {};
        Object.entries(backendErrors).forEach(([key, messages]) => {
          normalized[key] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setFieldErrors(normalized);
      } else {
        setSubmitError(t('error.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('title')}</h2>
      <p className={styles.description}>
        {t('description')}
      </p>

      {submitted && (
        <p className={styles.success}>
          {t('success')}<br />
          {t('inquiryCode', { code: inquiryCode || '...' })}<br />
          {t('confirmationEmail')}
        </p>
      )}

      {submitError && <p className={styles.error}>{submitError}</p>}

      <form onSubmit={handleSubmit}>
        <label className={styles.label} htmlFor="contact-name">
          {t('nameLabel')}
        </label>
        <input
          id="contact-name"
          type="text"
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          className={`${styles.input} ${fieldErrors.name ? styles.inputError : ''}`.trim()}
          placeholder={t('placeholderName')}
        />
        {fieldErrors.name && <p className={styles.fieldError}>{fieldErrors.name}</p>}

        <label className={styles.label} htmlFor="contact-email">
          {t('emailLabel')}
        </label>
        <input
          id="contact-email"
          type="email"
          value={form.email}
          onChange={(e) => onChange('email', e.target.value)}
          className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`.trim()}
          placeholder="example@irietix.com"
        />
        {fieldErrors.email && <p className={styles.fieldError}>{fieldErrors.email}</p>}

        <label className={styles.label} htmlFor="contact-category">
          {t('categoryLabel')}
        </label>
        <select
          id="contact-category"
          value={form.inquiry_type}
          onChange={(e) => onChange('inquiry_type', e.target.value)}
          className={`${styles.select} ${fieldErrors.inquiry_type ? styles.inputError : ''}`.trim()}
        >
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {fieldErrors.inquiry_type && <p className={styles.fieldError}>{fieldErrors.inquiry_type}</p>}

        <label className={styles.label} htmlFor="contact-subject">
          {t('subjectLabel')}
        </label>
        <input
          id="contact-subject"
          type="text"
          value={form.subject}
          onChange={(e) => onChange('subject', e.target.value)}
          className={`${styles.input} ${fieldErrors.subject ? styles.inputError : ''}`.trim()}
          placeholder={t('placeholderSubject')}
        />
        {fieldErrors.subject && <p className={styles.fieldError}>{fieldErrors.subject}</p>}

        <label className={styles.label} htmlFor="contact-message">
          {t('messageLabel')}
        </label>
        <textarea
          id="contact-message"
          value={form.message}
          onChange={(e) => onChange('message', e.target.value)}
          className={`${styles.textarea} ${fieldErrors.message ? styles.inputError : ''}`.trim()}
          placeholder={t('placeholderMessage')}
        />
        {fieldErrors.message && <p className={styles.fieldError}>{fieldErrors.message}</p>}

        <label className={styles.label} htmlFor="contact-event-url">
          {t('eventUrlLabel')}
        </label>
        <input
          id="contact-event-url"
          type="url"
          value={form.event_url}
          onChange={(e) => onChange('event_url', e.target.value)}
          className={`${styles.input} ${fieldErrors.event_url ? styles.inputError : ''}`.trim()}
          placeholder="https://irietix.com/events/sample-event"
        />
        {fieldErrors.event_url && <p className={styles.fieldError}>{fieldErrors.event_url}</p>}

        <p className={styles.notice}>
          {t('notice')}
        </p>

        <button type="submit" className={styles.submitButton} disabled={submitting}>
          {submitting ? t('submitting') : t('submit')}
        </button>
      </form>
    </div>
  );
};

export default ContactPage;
